import ModalWrapper from "./modalWrapper";
import {Stack, Typography, useTheme} from "@mui/material";
import React, {useEffect, useState} from "react";
import {PersonaType} from "../../common/enums/personaType.enum";
import Grid from "@mui/material/Unstable_Grid2";
import PersonaSlider from "../profile/personaSlider";
import {useIsMobile} from "../../common/hooks/useIsMobile";
import {UserCategoryResultPublic} from "../../common/models/userResults/userCategoryResultPublic.model";

interface PersonaModalProps {
    personaCategoryResults: UserCategoryResultPublic[],
    openModal: boolean,
    onClose: () => void
}

const PersonaModal = (props: PersonaModalProps) => {

    const {personaCategoryResults, openModal, onClose} = props;
    const theme = useTheme();
    const isMobile = useIsMobile();

    const [textColor, setTextColor] = useState<string>();
    const [iconBackgroundColour, setIconBackgroundColour] = useState<string | undefined>(theme.palette.thatsMe.surfacePrimary);
    const [modalColour, setModalColour] = useState<string | undefined>(theme.palette.thatsMe.surfacePrimary);
    const [persona, setPersona] = useState<UserCategoryResultPublic>();
    const [persona1, setPersona1] = useState<UserCategoryResultPublic>();
    const [persona2, setPersona2] = useState<UserCategoryResultPublic>();
    const [persona3, setPersona3] = useState<UserCategoryResultPublic>();

    useEffect(() => {

        if (!personaCategoryResults) {
            return;
        }

        const profilePersona = personaCategoryResults.find(ucr => ucr.categoryName === "Persona");

        if (!profilePersona) {
            return;
        }

        setPersona1(personaCategoryResults.find(r => r.categoryName === "Emotional Transparency"));
        setPersona2(personaCategoryResults.find(r => r.categoryName === "Stability"));
        setPersona3(personaCategoryResults.find(r => r.categoryName === "Emotional Disposition"));

        switch (profilePersona.response.toLowerCase()) {
            case PersonaType.DrizzlyEvening: {
                setModalColour(theme.palette.thatsMe.pageBackground?.drizzlyEvening);
                setTextColor(theme.palette.thatsMe.textPrimaryInvert);
                setIconBackgroundColour(theme.palette.thatsMe.pageBackground?.drizzlyEvening);
                break;
            }
            case PersonaType.MoonlessNight: {
                setModalColour(theme.palette.thatsMe.pageBackground?.moonlessNight);
                setTextColor(theme.palette.thatsMe.textPrimaryInvert);
                setIconBackgroundColour(theme.palette.thatsMe.pageBackground?.moonlessNight);
                break;
            }
            case PersonaType.ShootingStar: {
                setModalColour(theme.palette.thatsMe.pageBackground?.shootingStar);
                setTextColor(theme.palette.thatsMe.textPrimary);
                setIconBackgroundColour(theme.palette.thatsMe.pageBackground?.shootingStar);
                break;
            }
            case PersonaType.PassingHurricane: {
                setModalColour(theme.palette.thatsMe.pageBackground?.passingHurricane);
                setTextColor(theme.palette.thatsMe.textPrimaryInvert);
                setIconBackgroundColour(theme.palette.thatsMe.pageBackground?.passingHurricane);
                break;
            }
            case PersonaType.BlossomWind: {
                setModalColour(theme.palette.thatsMe.pageBackground?.blossomWind);
                setTextColor(theme.palette.thatsMe.textPrimary);
                setIconBackgroundColour(theme.palette.thatsMe.pageBackground?.blossomWind);
                break;
            }
            case PersonaType.NoClouds: {
                setModalColour(theme.palette.thatsMe.pageBackground?.noClouds);
                setTextColor(theme.palette.thatsMe.textPrimary);
                setIconBackgroundColour(theme.palette.thatsMe.pageBackground?.noClouds);
                break;
            }
            case PersonaType.LeavesChange: {
                setModalColour(theme.palette.thatsMe.pageBackground?.leavesChange);
                setTextColor(theme.palette.thatsMe.textPrimary);
                setIconBackgroundColour(theme.palette.thatsMe.pageBackground?.leavesChange);
                break;
            }
            case PersonaType.DormantVolcano: {
                setModalColour(theme.palette.thatsMe.pageBackground?.dormantVolcano);
                setTextColor(theme.palette.thatsMe.textPrimaryInvert);
                setIconBackgroundColour(theme.palette.thatsMe.pageBackground?.dormantVolcano);
                break;
            }
            case PersonaType.BabblingBrook: {
                setModalColour(theme.palette.thatsMe.pageBackground?.babblingBrook);
                setTextColor(theme.palette.thatsMe.textPrimary);
                setIconBackgroundColour(theme.palette.thatsMe.pageBackground?.babblingBrook);
                break;
            }
            case PersonaType.SturdyOak: {
                setModalColour(theme.palette.thatsMe.pageBackground?.sturdyOak);
                setTextColor(theme.palette.thatsMe.textPrimaryInvert);
                setIconBackgroundColour(theme.palette.thatsMe.pageBackground?.sturdyOak);
                break;
            }
        }

        setPersona(profilePersona);
    }, [personaCategoryResults]);

    return (
        <ModalWrapper 
            openModal={openModal} 
            onClose={onClose} 
            color={modalColour} 
            closeIconColor={textColor} 
            closeIconBackgroundColor={iconBackgroundColour}
            modalFixedHeight={!isMobile ? "auto" : undefined}
        >
            <Stack
                flex={1}
                width={"100%"}
                justifyContent={isMobile ? "center" : undefined}
                textAlign="center"
                alignItems="center"
            >
                <Typography
                    variant={"h2"}
                    color={textColor}
                >
                    {persona?.response}
                </Typography>
                <Stack mt={2}>
                    {
                        persona1 && <Grid container width={"100%"} alignItems={"center"}>
                            <Grid container spacing={1} xs={12}>
                                <Grid xs display="flex" justifyContent="flex-end" spacing={0}>
                                    <Typography variant={"body1"} color={textColor}>Guarded</Typography>
                                </Grid>
                                <Grid display="flex" justifyContent="center" spacing={0}>
                                    <PersonaSlider value={Number(persona1.response)} min={1} max={5} width={60} colour={textColor}/>
                                </Grid>
                                <Grid xs display="flex" justifyContent="flex-start" spacing={0}>
                                    <Typography variant={"body1"} color={textColor}>Open</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                    {
                        persona2 && <Grid container width={"100%"} alignItems={"center"} pt={0.5}>
                            <Grid container spacing={1} xs={12}>
                                <Grid xs display="flex" justifyContent="flex-end" spacing={0}>
                                    <Typography variant={"body1"} color={textColor}>Steadfast</Typography>
                                </Grid>
                                <Grid display="flex" justifyContent="center" spacing={0}>
                                    <PersonaSlider value={Number(persona2.response)} min={1} max={5} width={60} colour={textColor}/>
                                </Grid>
                                <Grid xs display="flex" justifyContent="flex-start" spacing={0}>
                                    <Typography variant={"body1"} color={textColor}>Unpredictable</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                    {
                        persona3 && <Grid container width={"100%"} alignItems={"center"} pt={0.5}>
                            <Grid container spacing={1} xs={12}>
                                <Grid xs display="flex" justifyContent="flex-end" spacing={0}>
                                    <Typography variant={"body1"} color={textColor}>Melancholy</Typography>
                                </Grid>
                                <Grid display="flex" justifyContent="center" spacing={0}>
                                    <PersonaSlider value={Number(persona3.response)} min={1} max={5} width={60} colour={textColor}/>
                                </Grid>
                                <Grid xs display="flex" justifyContent="flex-start" spacing={0}>
                                    <Typography variant={"body1"} color={textColor}>Cheerful</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                </Stack>
                <Typography
                    variant={"body1"}
                    color={textColor}
                    mt={4}
                    whiteSpace={"pre-line"}
                >
                    {persona?.blurb}
                </Typography>
            </Stack>
        </ModalWrapper>
    );
}

export {PersonaModal};